import { useState, useEffect } from "react";
import { rtdb } from "../../services/firebase";
import { ChatBooking } from "../../types/db/chat";
import { WithID } from "../../types/db";
import { ChatBookingService } from "../../types/services/chat";

/** Booking info is the info of a booking with the booking id */
type BookingInfo = WithID<ChatBooking>;

/**
 * Hook which takes care of fetching and updating the booking info of a chat
 * @param bookingID The booking ID
 * @returns the booking info
 */
export const useChatBooking = (bookingId: string): {
    /** The booking info */
    booking: BookingInfo | null;
    /** If any action is currently loading */
    isLoading?: boolean;
    /** If any action ended up with an error */
    error?: string;
} => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [booking, setBooking] = useState<BookingInfo | null>(null);

    useEffect(() => {
        if (!bookingId) {
            //if no listingId no need to go farther
            setIsLoading(false);
            return;
        }
        //update the current booking with the booking info from BE
        setIsLoading(true);
        //subscribe to any child/info of that booking
        const fetchBookingAbout = (new ChatBookingService(rtdb)).getEntityRef(bookingId);

        fetchBookingAbout
            .on("value", (snapshot) => {
                const bookingAbout: ChatBooking | null = snapshot.val();
                if (!snapshot.exists() || !bookingAbout) {
                    //can happen if doesn't exist
                    setError('Booking not found');
                    setBooking(null);
                    return;
                }
                setBooking({ ...bookingAbout, id: bookingId });
                setIsLoading(false);
            });

        return () => {
            //unsubscribe to all listener for the booking
            fetchBookingAbout.off();
            //reset the booking info
            setBooking(null);
        };
    }, [bookingId]);

    useEffect(() => {
        //from the nature of the subscription, it is possible that the booking doesn't exist
        //so `setIsLoading(false)` never called, therefore we add a timeout
        setTimeout(() => setIsLoading(false), 1000)
    }, []);

    return {
        booking,
        isLoading,
        error,
    }
}